import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  ProgressBar, Link, Icon, Title,
} from '@express-labs/raven-ui';
import { doEncryptLocal, cn } from '@express-labs/raven-tools';

import CircleProgressBar from '../CircleProgressBar';
import MyInsiderIDModal from '../MyInsiderIDModal';
import PromoCodeCarousel from '../PromoCode/PromoCodeCarousel';
import s from './AccountPanel.module.scss';
import classMouseLeave from '../../../../DesktopHeader/DesktopHeader.module.scss';
import { LOCAL_STORAGE_SECRET } from '../../../../../../constants';
import getFromStorage from '../../../../../../helpers/storage/storage';

const { body } = document;
class AccountPanel extends Component {
  static meta = {
    VERSION: '1.0.0',
  };

  static propTypes = {
    customerData: PropTypes.shape({
      firstName: PropTypes.string,
      tierName: PropTypes.string,
      pointsBalance: PropTypes.number,
      pointsToNextTier: PropTypes.number,
      totalPointsForNextReward: PropTypes.number,
      rewardsTotal: PropTypes.number,
      associateLogin: PropTypes.bool,
      sellerId: PropTypes.string,
    }).isRequired,
    tierIcon: PropTypes.string,
    keepStatus: PropTypes.bool,
    hasNextTier: PropTypes.bool,
    memberWalletEnhancement: PropTypes.bool,
    loyaltyRewards: PropTypes.shape({
      rewardsTotal: PropTypes.number,
    }).isRequired,
    nextTierName: PropTypes.string,
    tierExpirationDate: PropTypes.string,
    pointsToRetainTier: PropTypes.number,
    pointsForNextTier: PropTypes.number,
    removeScrollFreeze: PropTypes.func,
  };

  static defaultProps = {
    tierIcon: '',
    keepStatus: false,
    hasNextTier: false,
    nextTierName: '',
    tierExpirationDate: null,
    pointsToRetainTier: null,
    pointsForNextTier: 2500,
    memberWalletEnhancement: false,
    removeScrollFreeze: null,
  };

  static getLocalStorageData = (key) => key && localStorage.getItem(key);

  constructor(props) {
    super(props);
    this.state = {
      warmCustomer: null,
      showModalID: false,
    };
    this.toggleModal = this.toggleModal.bind(this);
  }

  componentDidMount() {
    if (window.hasStorage) {
      const encFirstName = AccountPanel.getLocalStorageData('firstName');
      const decryptedFirstName = encFirstName
        && doEncryptLocal.doDecrypt(LOCAL_STORAGE_SECRET, encFirstName);
      const loyaltyWarmStateData = getFromStorage('loyaltyWarmStateData', {});
      const warmCustomer = {
        firstName: decryptedFirstName,
        ...loyaltyWarmStateData,
      };
      this.setState({ warmCustomer });
    }
  }

  getTierStatus(myData) {
    const {
      keepStatus,
      hasNextTier,
      nextTierName,
      tierExpirationDate,
      pointsToRetainTier,
      pointsForNextTier,
      memberWalletEnhancement,
      customerData,
    } = this.props;
    const {
      pointsToNextTier,
      totalPointsForNextReward,
      pointsBalance,
      tierName,
      loyaltyNumber,
    } = myData;
    const emptyStateBalanceValue = totalPointsForNextReward * 0.01;
    const completed = pointsBalance === 0 ? emptyStateBalanceValue : pointsBalance;
    const isAssociate = customerData?.associateLogin || customerData?.sellerId;
    return (
      <>
        {hasNextTier ? (
          <>
            <p className={s.nextTierText}>
              You&apos;re
              <b>{` ${pointsToNextTier} `}</b>
              points away
              <br />
              from
              {' '}
              {nextTierName}
              {' '}
              status.
              {' '}
              <Link
                as="a"
                size="body"
                theme="light"
                href="/my-account/my-rewards"
                className={s.earnMoreLink}
              >
                Earn more
              </Link>
            </p>
            <ProgressBar
              className={s.progressBar}
              goal={pointsForNextTier}
              completed={completed}
              height={4}
              width={378}
              percentInContainer={100}
              backgroundColor="#575959"
              meterColor="white"
            />
            <div className={cn(s.tierBarDesc, 'rvnEyebrow')}>
              <span>{tierName}</span>
              <span>{nextTierName}</span>
            </div>
          </>
        ) : (
          <>
            <p className={s.subCopy}>Level up your points to get more perks.</p>
            <Link
              as="a"
              size="body"
              theme="light"
              href="/my-account/my-rewards"
              className={s.earnMoreButton}
            >
              earn more
            </Link>
          </>
        )}
        {tierExpirationDate && tierName !== 'Insider' && (
          <p className={s.tierExpires}>
            Your status expires on
            {' '}
            {tierExpirationDate}
            .
            <br />
            {keepStatus && `Earn ${pointsToRetainTier} points to keep your status.`}
          </p>
        )}
        {memberWalletEnhancement && (
        <PromoCodeCarousel
          memberWalletEnhancement={memberWalletEnhancement}
          loyaltyId={loyaltyNumber}
          isAssociate={isAssociate}
        />
        )}
      </>
    );
  }

  toggleModal() {
    if (this.state.showModalID && body) {
      body.style.paddingRight = '0px';
      body.classList.remove(classMouseLeave.scrollFreeze);
      const { removeScrollFreeze } = this.props;
      if (typeof removeScrollFreeze === 'function') {
        removeScrollFreeze();
      }
    }
    this.setState((prevState) => ({ showModalID: !prevState.showModalID }));
  }

  render() {
    const { customerData, tierIcon, loyaltyRewards } = this.props;
    const myData = this.state.warmCustomer ? this.state.warmCustomer : customerData;
    const {
      tierName, firstName, totalPointsForNextReward, pointsBalance,
    } = myData;

    const rewardsTotal = loyaltyRewards?.rewardsTotal;

    return (
      <div className={s.container} aria-label="account panel">
        <div className={s.nameRewardContainer}>
          <div className={s.titleWrapper}>
            <div className={s.nameWrapper} data-id="tier-icon-and-name-container">
              <div className={s.iconContainer}>
                <Icon icon={tierIcon} />
              </div>
              <Title tag="h3" className={s.userName} data-qm-encrypt>{firstName}</Title>
            </div>
            <Link
              as="a"
              theme="light"
              size="body"
              onClick={this.toggleModal}
              className={s.aListText}
              data-testid="profileModal-link"
            >
              My
              {' '}
              {tierName === 'A List' ? 'A-List' : tierName}
              {' '}
              ID
            </Link>
          </div>
          <div className={s.rewardsSubwrapper}>
            {rewardsTotal ? (
              <div className={s.rewardAmountWrapper}>
                <Icon icon="rewardStarLight" />
                <Title tag="h4" className={s.amountText}>{`$${rewardsTotal}`}</Title>
              </div>
            ) : (
              <CircleProgressBar
                goal={totalPointsForNextReward}
                completed={pointsBalance}
                aria-label="basic"
                useRewardIcon
              />
            )}
          </div>
        </div>
        {this.getTierStatus(myData)}
        <MyInsiderIDModal isOpen={this.state.showModalID} onModalClose={this.toggleModal} />
      </div>
    );
  }
}

export default AccountPanel;
